import { api, formatTenne } from "@awtoban/shared";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { Locale } from "../../i18n";
import { bcp47, HqHeader } from "../../lib/HqHeader";

interface OperatorRow {
  operator_id: number;
  operator: string;
  tx_count: number;
  void_count: number;
  refusal_count: number;
  avg_total_tenne: number;
  avg_mass_tn: number;
  oversized_count: number;
  cash_total_tenne: number;
  account_total_tenne: number;
}

export function IntegrityPage() {
  const { t, i18n } = useTranslation();
  const money = bcp47(i18n.language as Locale);

  const rows = useQuery({
    queryKey: ["hq-integrity-operators"],
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/hq/integrity/operators", {});
      if (error || !data) throw new Error("integrity_failed");
      return data as unknown as OperatorRow[];
    },
  });

  return (
    <div className="min-h-screen bg-paper-soft">
      <HqHeader active="integrity" />
      <main className="mx-auto max-w-[1100px] space-y-4 px-4 py-4 sm:px-6 sm:py-6">
        <p className="text-[13px] text-ink-muted">{t("integrity.intro")}</p>
        <section className="rounded-md border border-line-strong bg-paper">
          <h2 className="border-line border-b px-4 py-2 font-medium text-ink-muted text-xs uppercase tracking-wider">
            {t("integrity.title")}
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-[13px]">
              <thead>
                <tr className="border-line border-b text-[11px] text-ink-muted uppercase tracking-wider">
                  <th className="px-3 py-2 text-left">{t("integrity.operator")}</th>
                  <th className="px-3 py-2 text-right">{t("integrity.tx_count")}</th>
                  <th className="px-3 py-2 text-right">{t("integrity.avg_mass")}</th>
                  <th className="px-3 py-2 text-right">{t("integrity.avg_fee")}</th>
                  <th className="px-3 py-2 text-right">{t("integrity.oversized")}</th>
                  <th className="px-3 py-2 text-right">{t("integrity.voids")}</th>
                  <th className="px-3 py-2 text-right">{t("integrity.refusals")}</th>
                  <th className="px-3 py-2 text-right">{t("integrity.cash")}</th>
                  <th className="px-3 py-2 text-right">{t("integrity.account")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.isLoading ? (
                  <tr>
                    <td colSpan={9} className="px-3 py-6 text-ink-muted text-sm">
                      {t("integrity.loading")}
                    </td>
                  </tr>
                ) : (rows.data ?? []).length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-3 py-6 text-ink-muted text-sm">
                      {t("integrity.empty")}
                    </td>
                  </tr>
                ) : (
                  (rows.data ?? []).map((r) => (
                    <tr key={r.operator_id} className="border-line border-b last:border-b-0">
                      <td className="px-3 py-2 font-medium">{r.operator}</td>
                      <td className="tabular px-3 py-2 text-right">{r.tx_count}</td>
                      <td className="tabular px-3 py-2 text-right">{r.avg_mass_tn.toFixed(1)}</td>
                      <td className="tabular px-3 py-2 text-right">
                        {formatTenne(r.avg_total_tenne, money)}
                      </td>
                      <td className="tabular px-3 py-2 text-right">{r.oversized_count}</td>
                      <td
                        className={`tabular px-3 py-2 text-right ${r.void_count > 0 ? "text-danger" : ""}`}
                      >
                        {r.void_count}
                      </td>
                      <td
                        className={`tabular px-3 py-2 text-right ${r.refusal_count > 0 ? "text-warning-fg" : ""}`}
                      >
                        {r.refusal_count}
                      </td>
                      <td className="tabular px-3 py-2 text-right">
                        {formatTenne(r.cash_total_tenne, money)}
                      </td>
                      <td className="tabular px-3 py-2 text-right">
                        {formatTenne(r.account_total_tenne, money)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}
